import React from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { Checkbox, FormGroup, Select } from "@cofano/cck";
import { SelectProps } from "@cofano/cck/Select/Select";
import { Grid2 as Grid, Typography } from "@mui/material";

import { capitalize } from "components/selects/utils";
import TimePicker from "components/cck/TimePicker";

import { Language, hasOptionalFieldPermission, Fields } from "imports/Utils";

import { getAvailabilityHoursPerDay } from "view/ships/utils/ShipUtils";
import { ShipSchemaInput, ShipSchemaOutput } from "view/ships/utils/ship.schema";

import { Day } from "types/enums";
import { AvailabilityEnum } from "__generated__/enums";

const ShipAvailabilityForm = () => {

	const session = useStoreState(store => store.session);

	const { control, setValue } = useFormContext<ShipSchemaInput, unknown, ShipSchemaOutput>();

	const availableSunday = useWatch({ control, name: "availability.availableSunday" });
	const availableSaturday = useWatch({ control, name: "availability.availableSaturday" });

	return (
		<React.Fragment>
			<FormGroup label={Language("SHIP_AVAILABLE_DAYS")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="availability.availableMonday"
					control={control}
					render={() => {
						const value = availableSunday ? 3 : (availableSaturday ? 2 : 1);

						return (
							<AvailableDaysSelect
								value={value}
								multiple={false}
								onChange={(id) => {
									setValue("availability.availableSaturday", (id === 3) ? true : (id === 2));
									setValue("availability.availableSunday", (id === 3) ? true : ((id === 2) ? false : false));
								}} />
						);
					}} />
			</FormGroup>

			{hasOptionalFieldPermission(session, Fields.SHIP, "availableHoursADay") && (
				<FormGroup label={Language("SHIP_AVAILABLE_HOURS_A_DAY")} labelPlacement="left-center" overflow="hidden">
					<Controller
						name="availability.availableHoursADay"
						control={control}
						render={({ field }) => <AvailableHoursADaySelect {...field} />} />
				</FormGroup>
			)}

			<FormGroup label="" labelPlacement="left-center" overflow="hidden">
				<Controller
					name="availability.availableDayNight"
					control={control}
					render={({ field }) => <AvailableHourDayNightSelect {...field} />} />
			</FormGroup>

			<FormGroup label={Language("SHIP_AVAILABILITY")} labelPlacement="left-center" overflow="hidden">
				<Controller
					name="availability.defaultAvailability"
					control={control}
					render={({ field }) => (
						<Checkbox
							{...field}
							value={undefined}
							checked={field.value}
							label={Language("SHIP_DEFAULT_AVAILABLE_TRUE")} />
					)} />
			</FormGroup>

			<React.Fragment>
				<Typography
					variant="h4"
					fontWeight="medium"
					fontSize="18px"
					color="text.primary"
					mt={5}
					mb={2}>
					{Language("WORKING_HOURS")}
				</Typography>

				<React.Fragment>
					{Object.values(Day).map(day => (
						<FormGroup
							label={intl.formatMessage({ id: day.toUpperCase() })}
							labelPlacement="left-center"
							overflow="hidden"
							key={day}>
							<Grid spacing={3} container>
								<Grid size={6}>
									<Controller
										name={`workingHours.${day}From`}
										control={control}
										render={({ field }) => <TimePicker {...field} placeholderText={intl.formatMessage({ id: "FROM" })} />} />
								</Grid>

								<Grid size={6}>
									<Controller
										name={`workingHours.${day}To`}
										control={control}
										render={({ field }) => <TimePicker {...field} placeholderText={intl.formatMessage({ id: "TO" })} />} />
								</Grid>
							</Grid>
						</FormGroup>
					))}
				</React.Fragment>
			</React.Fragment>
		</React.Fragment>
	);
};

const AvailableDaysSelect = ({ value, onChange }: Omit<SelectProps<number, false>, "fetchOptions">) => {

	return (
		<Select
			value={value}
			fetchOptions={[ ...Array(3).keys() ].map(k => k + 1)}
			getOptionLabel={option => intl.formatMessage({ id: `SHIP_AVAILABLEDAYS_${option}` })}
			onChange={onChange}
			clearable={false}
			disableSearch />
	);
};

const AvailableHoursADaySelect = ({ value, onChange }: Omit<SelectProps<number, false>, "fetchOptions">) => {

	const { settings } = useStoreState(store => store.session);

	const availabilityHoursPerDay = getAvailabilityHoursPerDay(settings);

	return (
		<Select
			value={value}
			fetchOptions={availabilityHoursPerDay.map(n => Number(n))}
			onChange={onChange}
			clearable
			disableSearch />
	);
};

const AvailableHourDayNightSelect = ({ value, onChange }: Omit<SelectProps<AvailabilityEnum, false>, "fetchOptions">) => {
	return (
		<Select
			multiple={false}
			value={value}
			fetchOptions={Object.values(AvailabilityEnum)}
			onChange={onChange}
			clearable={false}
			getOptionLabel={option => capitalize(intl.formatMessage({ id: option }))}
			disableSearch />
	);
};

export default ShipAvailabilityForm;
